import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { API, formatApiError } from "../lib/api";
import { PORTAL_ENABLED } from "../lib/config";
import { Skull, Loader2, Check, ShieldAlert, FileText, X } from "lucide-react";

const Shell = ({ children, wide }) => (
  <div className="min-h-screen bg-[#050505] text-[#F2EBE1] flex items-center justify-center p-6">
    <div className={wide ? "w-full max-w-2xl" : "w-full max-w-md"}>
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3">
          <Skull size={28} className="text-blood" strokeWidth={1.5} />
          <span className="font-display text-2xl font-bold tracking-[0.04em] text-blood">SCARE AUDITIONS<span className="text-[0.45em] font-semibold tracking-normal align-top opacity-80 ml-1">.co.uk</span></span>
        </div>
      </div>
      {children}
    </div>
  </div>
);

export default function RoleOffer() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [step, setStep] = useState("confirm"); // confirm | contract | decline
  const [scrolledBottom, setScrolledBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // "accepted" | "declined" | "availability_changed"
  const [error, setError] = useState(null);
  const [availChoice, setAvailChoice] = useState(null); // "same" | "changed"
  const scrollRef = useRef(null);

  const fmtDate = (d) => {
    if (!d) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  };

  const submitAvailabilityChanged = async () => {
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API}/public/role-offer/${token}/availability-changed`, { note: reason });
      setResult("availability_changed");
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  useEffect(() => {
    axios.get(`${API}/public/role-offer/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setLoadError(formatApiError(e)));
  }, [token]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledBottom(true);
  };

  // If the contract is short enough not to scroll, allow agreement immediately.
  useEffect(() => {
    if (step !== "contract") return;
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 24) setScrolledBottom(true);
  }, [step, data]);

  const accept = async () => {
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API}/public/role-offer/${token}/accept`);
      setResult("accepted");
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const decline = async () => {
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API}/public/role-offer/${token}/decline`, { reason });
      setResult("declined");
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (loadError) {
    return (
      <Shell>
        <div className="border border-blood/40 bg-blood/10 p-8 text-center" data-testid="role-offer-error">
          <ShieldAlert size={28} className="text-blood mx-auto mb-3" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Link Issue</h1>
          <p className="text-sm text-[#A19D94] mt-2">{loadError || "This role offer link is invalid or has expired. Please contact the organisation if you think this is a mistake."}</p>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return <Shell><div className="flex justify-center py-10"><Loader2 className="animate-spin text-blood" size={28} /></div></Shell>;
  }

  // Final result screens
  if (result === "accepted") {
    return (
      <Shell>
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-8 text-center" data-testid="role-offer-accepted-msg">
          <Check size={30} className="text-[#22C55E] mx-auto mb-3" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Welcome to the Cast</h1>
          <p className="text-sm text-[#A19D94] mt-2">Welcome to the cast{data.first_name ? `, ${data.first_name}` : ""}. Your role and contract are confirmed.{PORTAL_ENABLED ? " Check your email for confirmation and to set up your Actor Portal." : ""}</p>
        </div>
      </Shell>
    );
  }
  if (result === "declined") {
    return (
      <Shell>
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-8 text-center" data-testid="role-offer-declined-msg">
          <X size={30} className="text-[#A19D94] mx-auto mb-3" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Thank You</h1>
          <p className="text-sm text-[#A19D94] mt-2">Thank you for letting us know{data.first_name ? `, ${data.first_name}` : ""}. We hope to see you in a future season.</p>
        </div>
      </Shell>
    );
  }
  if (result === "availability_changed") {
    return (
      <Shell>
        <div className="border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-8 text-center" data-testid="role-offer-availability-changed-msg">
          <ShieldAlert size={30} className="text-[#F59E0B] mx-auto mb-3" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Thanks — We'll Be In Touch</h1>
          <p className="text-sm text-[#A19D94] mt-2">Thanks for letting us know{data.first_name ? `, ${data.first_name}` : ""}. Because your availability has changed, your role hasn't been confirmed automatically. A member of the team will be in touch to sort out your dates and finalise your role.</p>
        </div>
      </Shell>
    );
  }

  // Already responded
  if (data.used) {
    const usedMsg = data.outcome === "declined"
      ? "You have already declined this role offer."
      : data.outcome === "availability_changed"
      ? "You've let us know your availability has changed — a member of the team will be in touch."
      : "You have already accepted this role offer.";
    return (
      <Shell>
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-8 text-center" data-testid="role-offer-used-msg">
          <Check size={30} className={`mx-auto mb-3 ${data.outcome === "declined" ? "text-[#A19D94]" : data.outcome === "availability_changed" ? "text-[#F59E0B]" : "text-[#22C55E]"}`} />
          <h1 className="font-display text-2xl uppercase tracking-tight">Already Responded</h1>
          <p className="text-sm text-[#A19D94] mt-2">{usedMsg}</p>
        </div>
      </Shell>
    );
  }

  // Step 1 — confirm
  if (step === "confirm") {
    return (
      <Shell>
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-8" data-testid="role-offer-confirm">
          <h1 className="font-display text-2xl font-black uppercase tracking-tight text-center leading-tight">
            You've been offered the role{(data.roles || []).length > 1 ? "s" : ""} of <span className="text-blood">{data.role_name}</span>
          </h1>
          <p className="text-center text-[#A19D94] text-sm mt-2">{data.event_name}</p>
          <div className="mt-6 space-y-3" data-testid="role-offer-roles">
            {((data.roles && data.roles.length) ? data.roles : [{ name: data.role_name, character_name: data.character_name, department: data.role_department, placement: data.role_placement }]).map((r, i) => (
              <div key={i} className="border-l-2 border-blood pl-4 space-y-1 text-sm" data-testid={`role-offer-role-${i}`}>
                <div className="text-[#F2EBE1] font-medium">{r.name}</div>
                {r.character_name && r.character_name !== r.name && <div className="text-[#A19D94]">{r.character_name}</div>}
                {r.department && <div className="text-[#6B6862] text-xs uppercase tracking-wider">{r.department}</div>}
                {r.placement && <div className="text-[#6B6862] text-xs">{r.placement}</div>}
              </div>
            ))}
          </div>

          {/* Availability re-confirmation */}
          <div className="mt-7 border-t border-[#1a1a1a] pt-5" data-testid="role-offer-availability">
            <p className="text-xs uppercase tracking-wider text-[#8a857d] mb-2">You told us you're available on these nights</p>
            {(data.available_dates || []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {data.available_dates.map((d) => (
                  <span key={d.id} data-testid={`role-offer-availdate-${d.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-[#262626] bg-[#050505] text-[#D6CFC4]">
                    {fmtDate(d.date)}{d.name ? <span className="text-[#6B6862]">· {d.name}</span> : null}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#6B6862] mb-4">No specific dates on file.</p>
            )}
            <div className="space-y-2">
              <label className={`flex items-start gap-2.5 cursor-pointer p-2.5 border ${availChoice === "same" ? "border-[#16A34A]/60 bg-[#16A34A]/10" : "border-[#262626]"}`} data-testid="role-offer-avail-same">
                <input type="radio" name="avail" checked={availChoice === "same"} onChange={() => setAvailChoice("same")} className="accent-[#16A34A] mt-0.5" />
                <span className="text-sm text-[#F2EBE1]">I'm still available for all of these nights.</span>
              </label>
              <label className={`flex items-start gap-2.5 cursor-pointer p-2.5 border ${availChoice === "changed" ? "border-[#F59E0B]/60 bg-[#F59E0B]/10" : "border-[#262626]"}`} data-testid="role-offer-avail-changed">
                <input type="radio" name="avail" checked={availChoice === "changed"} onChange={() => setAvailChoice("changed")} className="accent-[#F59E0B] mt-0.5" />
                <span className="text-sm text-[#F2EBE1]">My availability has changed.</span>
              </label>
            </div>
          </div>

          {error && <div className="mt-4 p-3 border border-blood/40 bg-blood/10 text-sm text-blood">{error}</div>}

          {availChoice === "changed" && (
            <div className="mt-4 p-4 border border-[#F59E0B]/40 bg-[#F59E0B]/10" data-testid="role-offer-avail-warning">
              <div className="flex items-start gap-2.5">
                <ShieldAlert size={18} className="text-[#F59E0B] shrink-0 mt-0.5" />
                <p className="text-sm text-[#E8DFD0]">Because your availability has changed, the role you've been offered may be affected — it was based on the dates you originally gave us. <strong>Your role won't be assigned automatically.</strong> Let us know below and a member of the team will be in touch to sort out your dates.</p>
              </div>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="role-offer-avail-note" rows={3}
                placeholder="Optional: tell us which nights have changed…"
                className="w-full mt-3 bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#F59E0B] resize-none" />
            </div>
          )}

          <div className="mt-6 space-y-2">
            {availChoice === "changed" ? (
              <button onClick={submitAvailabilityChanged} disabled={submitting} data-testid="role-offer-notify-team-btn"
                className="w-full py-3 bg-[#F59E0B] text-[#0A0A0A] font-display uppercase tracking-wider hover:bg-[#FBBF24] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                {submitting && <Loader2 size={14} className="animate-spin" />} Let The Team Know
              </button>
            ) : (
              <button onClick={() => setStep("contract")} disabled={availChoice !== "same"} data-testid="role-offer-accept-btn"
                className="w-full py-3 bg-[#16A34A] text-white font-display uppercase tracking-wider hover:bg-[#15803D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                Accept Role
              </button>
            )}
            <button onClick={() => setStep("decline")} data-testid="role-offer-decline-btn"
              className="w-full py-2.5 border border-[#262626] text-[#A19D94] text-sm uppercase tracking-wider hover:border-[#3a3835] hover:text-[#F2EBE1] transition-colors">
              Decline Role
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  // Step 2b — decline form
  if (step === "decline") {
    return (
      <Shell>
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-8" data-testid="role-offer-decline-form">
          <h1 className="font-display text-xl uppercase tracking-tight">Decline Role</h1>
          <p className="text-sm text-[#A19D94] mt-2">We're sorry to hear that. Please let us know if you'd like to share a reason (optional).</p>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="role-offer-decline-reason"
            rows={4} placeholder="Reason for declining (optional)"
            className="w-full mt-4 bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood resize-none" />
          {error && <div className="mt-4 p-3 border border-blood/40 bg-blood/10 text-sm text-blood">{error}</div>}
          <div className="mt-5 flex items-center gap-2">
            <button onClick={() => { setStep("confirm"); setError(null); }} className="flex-1 py-2.5 border border-[#262626] text-[#A19D94] text-xs uppercase tracking-wider hover:border-[#3a3835]">Go Back</button>
            <button onClick={decline} disabled={submitting} data-testid="role-offer-confirm-decline-btn"
              className="flex-1 py-2.5 bg-blood text-white text-xs uppercase tracking-wider hover:bg-[#FF3333] disabled:opacity-40 flex items-center justify-center gap-2">
              {submitting && <Loader2 size={13} className="animate-spin" />} Confirm Decline
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  // Step 2a — contract
  return (
    <Shell wide>
      <div className="text-center mb-5">
        <h1 className="font-display text-2xl font-black uppercase tracking-tight">Before we confirm your place</h1>
        <p className="text-[#A19D94] text-sm mt-2">Please read and agree to your contract for <span className="text-blood">{data.role_name}</span></p>
      </div>
      <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-6">
        {!data.contract_configured ? (
          <div className="text-center py-10" data-testid="role-offer-no-contract">
            <ShieldAlert size={32} className="text-[#F59E0B] mx-auto mb-3" />
            <h2 className="font-display text-lg uppercase tracking-tight text-[#F2EBE1]">Contract not available yet</h2>
            <p className="text-sm text-[#A19D94] mt-2 max-w-md mx-auto">Your contract isn't ready to view just yet. Please check back shortly or contact the team — you'll be able to accept your role once the contract has been published.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3 text-[#A19D94]"><FileText size={16} className="text-blood" /><span className="text-xs uppercase tracking-wider">Please read the full contract</span></div>
            <div ref={scrollRef} onScroll={onScroll} data-testid="role-offer-contract-text"
              className="rte-content h-80 overflow-y-auto border border-[#262626] bg-[#050505] p-4 text-sm"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.contract_text || "") }} />
            {!scrolledBottom && <p className="text-[11px] text-[#F59E0B] mt-2" data-testid="role-offer-scroll-hint">↓ Scroll to the bottom of the contract to continue.</p>}

            {error && <div className="mt-4 p-3 border border-blood/40 bg-blood/10 text-sm text-blood">{error}</div>}

            <div className="mt-5 flex items-center gap-2 text-[13px] text-[#A19D94]" data-testid="role-offer-pdf-note">
              <FileText size={14} className="text-blood shrink-0" />
              <span>A copy of this contract will also be emailed to you in PDF format.</span>
            </div>

            <label className={`flex items-start gap-2.5 mt-3 ${scrolledBottom ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`} data-testid="role-offer-agree">
              <input type="checkbox" disabled={!scrolledBottom} checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="accent-blood mt-0.5" />
              <span className="text-sm text-[#F2EBE1]">I have read and agree to the terms of this contract.</span>
            </label>

            <button onClick={accept} disabled={!scrolledBottom || !agreed || submitting} data-testid="role-offer-agree-confirm-btn"
              className="w-full mt-5 py-3 bg-blood text-white font-display uppercase tracking-wider hover:bg-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Agree &amp; Confirm
            </button>
          </>
        )}
      </div>
    </Shell>
  );
}
